import { useReactFlow } from '@xyflow/react';
import { memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { Button, Flex, Menu, MenuItem, MenuItemDelete, Popover, Save, Text } from '@/components/core';
import { NewTriggerModal, TriggerOption } from '@/components/flow';
import { AddIcon, OverflowIcon } from '@/components/icons';
import { useAddTrigger } from '@/hooks';
import { toAgentId, useDeleteAction, useSuspenseGetAgent, useUpdateAgent } from '@/hooks/api';

export type TriggerHeaderProps = {
  agentId: string;
  actionId: string;
  children?: ReactNode;
};

const ITriggerHeader = ({ agentId, actionId, children }: TriggerHeaderProps) => {
  const [newTriggerModelOpened, setNewTriggerModalOpened] = useState(false);
  const { data: agent } = useSuspenseGetAgent(toAgentId(agentId));
  const builder = useReactFlow();
  const onSuccess = useCallback(() => {
    builder.setNodes((nodes) => nodes);
    setNewTriggerModalOpened(false);
  }, [builder]);
  const { addTrigger, isPending: isAddingTrigger } = useAddTrigger({ onSuccess });

  const { mutate: updateAgent, isPending: isPendingUpdateAgent } = useUpdateAgent(toAgentId(agentId));
  const { mutate: deleteAction, isPending: isPendingDeleteAction } = useDeleteAction();

  const deleteTrigger = useCallback(() => {
    const updates = { resourceIds: agent.resourceIds.filter((r) => ![actionId].includes(r)) };
    updateAgent(
      { id: toAgentId(agentId), updates },
      {
        onSuccess: () => {
          // TODO: Need to also delete hooks
          deleteAction(
            { actionId },
            {
              onSuccess: () => {
                // TODO: Avoid force-triggering rerender of flow
                builder.setNodes((nodes) => nodes);
              },
            }
          );
        },
      }
    );
  }, [agent.resourceIds, updateAgent, agentId, actionId, deleteAction, builder]);

  const onTriggerOptionSelected = useCallback(
    async (triggerOption: TriggerOption) => {
      await addTrigger({ agentId, triggerOption });
    },
    [addTrigger, agentId]
  );

  const isDeleting = useMemo(
    () => isPendingDeleteAction || isPendingUpdateAgent,
    [isPendingDeleteAction, isPendingUpdateAgent]
  );

  // TODO: Figure out why uncontrolled doesn't work here
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Flex className="w-full">
      <Flex className="gap-sm grow items-center">{children}</Flex>
      {isDeleting ? (
        <Save message="Deleting" />
      ) : (
        <MenuTrigger>
          <Button variant="plain" onPress={() => setIsOpen(true)}>
            <OverflowIcon />
          </Button>

          <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
            <Menu>
              <MenuItemDelete onAction={deleteTrigger} />
              <MenuItem
                onAction={() => {
                  setNewTriggerModalOpened(true);
                  setIsOpen(false);
                }}
              >
                <AddIcon />
                <Text>New trigger</Text>
              </MenuItem>
            </Menu>
          </Popover>
        </MenuTrigger>
      )}
      <NewTriggerModal
        isAddingTrigger={isAddingTrigger}
        isOpen={newTriggerModelOpened}
        onOpenChange={() => setNewTriggerModalOpened(false)}
        onTriggerOptionSelected={onTriggerOptionSelected}
      />
    </Flex>
  );
};

export const TriggerHeader = memo(ITriggerHeader) as typeof ITriggerHeader;
